import { useEffect, useState } from 'react';
import { useReturnLocation } from '@shared-ui/navigation';
import { AttributeGrid, Attribute } from '@shared-ui/components';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, formatDistanceToNow } from 'date-fns';
import { adminApi } from '../../api/adminApi';
import { authApi } from '../../api/auth';
import { describeAcquisition } from '../../utils/acquisition';
import { getStudentAppUrl, getTeacherAppUrl } from '@shared-ui/api';
import { ActivityTab } from './tabs/ActivityTab';
import { EligibilityTab } from './tabs/EligibilityTab';
import { FeatureGatesTab } from './tabs/FeatureGatesTab';
import { SessionsTab } from './tabs/SessionsTab';
import { PatternsTab } from './tabs/PatternsTab';
import { ExperimentsTab } from './tabs/ExperimentsTab';
import { SubscriptionsTab } from './tabs/SubscriptionsTab';
import { RateLimitsTab } from './tabs/RateLimitsTab';
import { CostsTab } from './tabs/CostsTab';
import { LoggingTab } from './tabs/LoggingTab';
import { TeacherControlsTab } from './tabs/TeacherControlsTab';
import { TeacherStudentsTab } from './tabs/TeacherStudentsTab';
import { TeacherProfileTab } from './tabs/TeacherProfileTab';
import { HelpQueriesTab } from './tabs/HelpQueriesTab';
import { HandwritingTab } from './tabs/HandwritingTab';
import { isAdmin as checkIsAdmin, hasRole, userTypeFromRoles } from '../../utils/userRoles';
import { PUBLIC_TENANT_ID } from '../../api/tenantsApi';
import { EditRolesModal } from './EditRolesModal';
import { SendMessageModal } from './SendMessageModal';
import { SetPasswordModal } from './SetPasswordModal';
import type { User } from '../../types';
import './UserDetail.css';

/** Map the unified roles down to the 3-axis type RateLimitsTab expects;
 *  super-admin collapses to 'admin'. */
function rateLimitUserType(user: User | undefined): 'student' | 'teacher' | 'admin' {
  if (!user) return 'student';
  const t = userTypeFromRoles(user);
  return t === 'super-admin' ? 'admin' : t;
}

function formatLastActive(dateStr?: string): string {
  if (!dateStr) return 'Never';
  try {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
  } catch {
    return 'Unknown';
  }
}

export function UserDetail() {
  const { id: userId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  // The list's query string, stashed when navigating in, so Back restores the
  // same filtered/sorted/paged view instead of a reset /users list.
  const backSearch = (location.state as { backSearch?: string } | null)?.backSearch ?? '';
  // Opened from somewhere other than the list — Site usage's sessions, say —
  // the link says so (`returnTo`), and Back pops to exactly that page rather
  // than dropping the reader on the Users list.
  const returnTo = useReturnLocation();
  const goBackToList = () => (returnTo ? navigate(-1) : navigate(`/users${backSearch}`));

  const [activeTab, setActiveTab] = useState<'eligibility' | 'feature-gates' | 'experiments' | 'subscriptions' | 'rate-limits' | 'costs' | 'logging' | 'sessions' | 'patterns' | 'teacher-controls' | 'students' | 'help-queries' | 'teacher-profile' | 'activity' | 'handwriting'>('subscriptions');
  const [editRolesOpen, setEditRolesOpen] = useState(false);
  const [sendMessageOpen, setSendMessageOpen] = useState(false);
  const [setPasswordOpen, setSetPasswordOpen] = useState(false);

  const { data: user } = useQuery({
    queryKey: ['user-detail', userId],
    queryFn: () => adminApi.getUserById(userId!),
    enabled: !!userId,
  });

  const isTestUser = user?.email?.endsWith('@padcanvas.test');
  // A guest (nobody signed up — minted by a link or the science site) can be
  // hard-deleted by a tenant admin too; see admin/auth/deleteUser.ts.
  const isGuestUser = user?.ring === 'guest';

  const softDeleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.softDeleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user-detail', userId] });
    },
  });

  const hardDeleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user-detail', userId] });
      goBackToList();
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => adminApi.restoreUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user-detail', userId] });
    },
  });

  // Inline edit form: ring / pipeline / notification email + the student
  // profile fields (grade / target exams / subjects) are staged locally and
  // committed together by one PATCH on Save. "Edit" toggles editability.
  const [isEditing, setIsEditing] = useState(false);
  const [editRing, setEditRing] = useState<string>('');
  const [editName, setEditName] = useState<string>('');
  const [editPhone, setEditPhone] = useState<string>('');
  const [editPipelineConfig, setEditPipelineConfig] = useState<string>('');
  const [editNotificationEmail, setEditNotificationEmail] = useState<string>('');
  const [editGrade, setEditGrade] = useState<string>('');
  const [editTargetExams, setEditTargetExams] = useState<string>('');
  const [editSubjects, setEditSubjects] = useState<string>('');
  useEffect(() => {
    // Re-seed from the user whenever it (re)loads — but not while the admin is
    // mid-edit, so a background refetch doesn't clobber their input.
    if (isEditing) return;
    const exams = user?.targetExams?.length ? user.targetExams : (user?.primaryTargetExam ? [user.primaryTargetExam] : []);
    setEditRing(user?.ring || 'prod');
    setEditName(user?.name || '');
    setEditPhone(user?.phone || '');
    setEditPipelineConfig(user?.pipelineConfig || '');
    setEditNotificationEmail(user?.notificationEmail || '');
    setEditGrade(user?.grade != null ? String(user.grade) : '');
    setEditTargetExams(exams.join(', '));
    setEditSubjects((user?.subjects ?? []).join(', '));
  }, [user, isEditing]);

  const { data: pipelineConfigsData } = useQuery({
    queryKey: ['pipeline-configs'],
    queryFn: adminApi.listPipelineConfigs,
    staleTime: 60_000,
  });
  const pipelineConfigNames = (pipelineConfigsData?.configs || []).map(c => c.name);

  const parseCsv = (s: string) => s.split(',').map(x => x.trim()).filter(Boolean);

  const updateUserMutation = useMutation({
    mutationFn: () => {
      if (!user) return Promise.reject(new Error('user not loaded'));
      const patch: { ring?: string; name?: string; phone?: string | null; pipelineConfig?: string | null; notificationEmail?: string | null; grade?: number | null; targetExams?: string[]; subjects?: string[] } = {};
      if (editRing !== (user.ring || 'prod')) patch.ring = editRing;
      // Only a super-admin can send these — the backend rejects them outright
      // from anyone else, which would fail the whole PATCH and lose the other
      // edits staged alongside them.
      if (canEditIdentity) {
        const trimmedName = editName.trim();
        if (trimmedName !== (user.name || '') && trimmedName !== '') patch.name = trimmedName;
        const trimmedPhone = editPhone.trim();
        if (trimmedPhone !== (user.phone || '')) patch.phone = trimmedPhone === '' ? null : trimmedPhone;
        const trimmedNotificationEmail = editNotificationEmail.trim();
        if (trimmedNotificationEmail !== (user.notificationEmail || '')) {
          patch.notificationEmail = trimmedNotificationEmail === '' ? null : trimmedNotificationEmail;
        }
      }
      if (editPipelineConfig !== (user.pipelineConfig || '')) {
        patch.pipelineConfig = editPipelineConfig === '' ? null : editPipelineConfig;
      }
      const g = editGrade.trim() === '' ? null : parseInt(editGrade, 10);
      if (g !== (user.grade ?? null)) patch.grade = g;
      const exams = parseCsv(editTargetExams);
      const curExams = user.targetExams?.length ? user.targetExams : (user.primaryTargetExam ? [user.primaryTargetExam] : []);
      if (exams.join('||') !== curExams.join('||')) patch.targetExams = exams;
      const subs = parseCsv(editSubjects);
      if (subs.join('||') !== (user.subjects ?? []).join('||')) patch.subjects = subs;
      return adminApi.updateUser(userId!, patch);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user-detail', userId] });
      setIsEditing(false);
    },
  });

  const impersonateMutation = useMutation({
    mutationFn: () => adminApi.impersonateUser(userId!),
    onSuccess: (data) => {
      const baseUrl = user && hasRole(user, 'teacher') ? getTeacherAppUrl() : getStudentAppUrl();
      const url = `${baseUrl}/impersonate?token=${encodeURIComponent(data.token)}`;
      window.open(url, '_blank');
    },
  });

  const handleImpersonate = () => {
    if (!user) return;
    const appName = hasRole(user, 'teacher') ? 'teacher' : 'student';
    if (!window.confirm(`Start impersonating "${user.name}"? A new tab will open with their ${appName} session (read-only, 2h expiry).`)) return;
    impersonateMutation.mutate();
  };

  const handleDelete = () => {
    if (!user) return;
    if (isTestUser || (isGuestUser && viewerCanAdmin)) {
      const what = isTestUser ? 'test user' : 'guest';
      if (!window.confirm(`HARD DELETE ${what} "${user.email ?? user.name}"? This will permanently remove the account and all associated data.`)) return;
      hardDeleteMutation.mutate(user.id);
    } else {
      if (!window.confirm(`Deactivate user "${user.name}"? They will be unable to log in.`)) return;
      softDeleteMutation.mutate(user.id);
    }
  };

  const handleRestore = () => {
    if (!user) return;
    if (!window.confirm(`Restore user "${user.name}"? They will be able to log in again.`)) {
      return;
    }
    restoreMutation.mutate(user.id);
  };

  if (!userId) return <div className="error">No user ID provided</div>;

  // Viewer authority + per-target action rules — shared by the Roles edit icon
  // and the Impersonate/Delete action row. Gate to tenant_admin (or super-admin,
  // implicitly higher); other admin roles cannot see these, matching the
  // backend's auth checks.
  const me = authApi.getUser();
  const viewerCanAdmin = !!me && (me.isSuperAdmin === true || !!me.roles?.includes('tenant_admin'));
  // Platform viewer: a super-admin, or an admin of the public tenant. Two things
  // hang off this — impersonation, which mirrors the server rule in
  // admin/auth/impersonateUser.ts (handing a school's admin a child's session is
  // not a capability buying the product grants), and the tab strip below, which
  // is platform machinery end to end: eligibility, feature gates, experiments,
  // rate limits, model costs, logs. A customer's admin administers none of it
  // and can act on none of it.
  const viewerIsPlatform = !!me && (me.isSuperAdmin === true || me.tenantId === PUBLIC_TENANT_ID);
  const canImpersonate = !!user && !user.deletedAt && viewerCanAdmin && viewerIsPlatform && (
    me!.isSuperAdmin ? !user.roles?.includes('super_admin') : !checkIsAdmin(user)
  );
  // Editing roles: never for super_admin targets — the synthetic super-admin
  // authority must not be mutated through this endpoint.
  const canEditRoles = !!user && !user.deletedAt && viewerCanAdmin && !user.roles?.includes('super_admin');
  const canEditForm = !!user && !user.deletedAt;
  // Name + phone + notification email: super-admin only, mirroring the backend
  // guard in admin/auth/updateUser.ts. The latter two are the addresses
  // notifications deliver to, so repointing one redirects someone's mail —
  // not something every admin role should be able to do.
  const canEditIdentity = !!me?.isSuperAdmin;
  const IDENTITY_ONLY_SUPER_ADMIN =
    'Only a super-admin can change name, phone or notification email.';

  return (
    <div className="user-detail-page">
      <button className="ud-back-btn" onClick={goBackToList}>
        &larr; Back to {returnTo?.label ?? 'Users'}
      </button>

      {/* User Info Card */}
      {user ? (
        <div className="user-info-card">
          <div className="user-info-card-header">
            <h1>{user.name}</h1>
            <span className={`role-badge role-${userTypeFromRoles(user)}`}>{userTypeFromRoles(user)}</span>
            {user.bypassSubscription && <span className="bypass-badge">Bypass Subscription</span>}
            {/* Beside the name, not parked at the card's far edge: on a wide
                card the far edge was off-screen, and the one control that edits
                every field on this page is no good where nobody can see it. */}
            {canEditForm && (
              <div className="user-info-header-edit">
                {isEditing ? (
                  <>
                    {updateUserMutation.isError && (
                      <span className="status-error">Failed: {(updateUserMutation.error as Error).message}</span>
                    )}
                    <button className="btn btn-secondary" onClick={() => setIsEditing(false)} disabled={updateUserMutation.isPending}
                      style={{ padding: '6px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600 }}>
                      Cancel
                    </button>
                    <button className="btn btn-primary" onClick={() => updateUserMutation.mutate()} disabled={updateUserMutation.isPending}
                      style={{ padding: '6px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600 }}>
                      {updateUserMutation.isPending ? 'Saving...' : 'Save Changes'}
                    </button>
                  </>
                ) : (
                  <button className="btn btn-secondary" onClick={() => setIsEditing(true)}
                    style={{ padding: '6px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600 }}>
                    Edit
                  </button>
                )}
              </div>
            )}
          </div>

          <AttributeGrid>
            <Attribute label="Name">
              <span className="sui-attr-value ud-identity-value">
                {isEditing && canEditIdentity
                  ? <input type="text" className="ud-edit-input" value={editName} onChange={e => setEditName(e.target.value)} disabled={updateUserMutation.isPending} />
                  : user.name}
                {isEditing && (
                  <span className="ud-field-info" title={IDENTITY_ONLY_SUPER_ADMIN} aria-label={IDENTITY_ONLY_SUPER_ADMIN} role="img">i</span>
                )}
              </span>
            </Attribute>
            <Attribute label="Email">
              <span className="sui-attr-value">{user.email}</span>
            </Attribute>
            <Attribute label="Phone">
              <span className="sui-attr-value ud-identity-value">
                {isEditing && canEditIdentity
                  ? <input type="tel" className="ud-edit-input" value={editPhone} onChange={e => setEditPhone(e.target.value)} placeholder="+919876543210" pattern="\+[0-9]{7,15}" disabled={updateUserMutation.isPending} />
                  : (user.phone || <em style={{ color: 'var(--text-tertiary)' }}>no number</em>)}
                {isEditing && (
                  <span className="ud-field-info" title={IDENTITY_ONLY_SUPER_ADMIN} aria-label={IDENTITY_ONLY_SUPER_ADMIN} role="img">i</span>
                )}
              </span>
            </Attribute>
            {/* How they sign in — an email for anyone who registered with one, a
                provisioned handle (`stf-9.teoler`) for a roster account. The user
                id is machine identity and told a reader nothing they could act
                on; it is still in the page's own URL when a log line needs it. */}
            <Attribute label="Login ID">
              <span className="sui-attr-value sui-attr-mono">
                {user.loginId || <em style={{ color: 'var(--text-tertiary)' }}>none</em>}
              </span>
            </Attribute>
            {!checkIsAdmin(user) && (
              <>
                <Attribute label="Grade">
                  <span className="sui-attr-value">
                    {isEditing
                      ? <input type="number" min={1} max={12} className="ud-edit-input" style={{ maxWidth: 90 }} value={editGrade} onChange={e => setEditGrade(e.target.value)} placeholder="1–12" />
                      : (user.grade ?? '-')}
                  </span>
            </Attribute>
                <Attribute label="Target Exams">
                  <span className="sui-attr-value">
                    {isEditing
                      ? <input type="text" className="ud-edit-input" value={editTargetExams} onChange={e => setEditTargetExams(e.target.value)} placeholder="comma-separated, e.g. JEE, NEET" />
                      : (user.targetExams?.length ? user.targetExams.join(', ') : user.primaryTargetExam || '-')}
                  </span>
            </Attribute>
                <Attribute label="Subjects">
                  <span className="sui-attr-value">
                    {isEditing
                      ? <input type="text" className="ud-edit-input" value={editSubjects} onChange={e => setEditSubjects(e.target.value)} placeholder="comma-separated, e.g. physics, chemistry" />
                      : (user.subjects?.length ? user.subjects.join(', ') : '-')}
                  </span>
            </Attribute>
                {!hasRole(user, 'teacher') && user.teachers && user.teachers.length > 0 && (
                  <Attribute label={user.teachers.length > 1 ? 'Teachers' : 'Teacher'}>
                    <span className="sui-attr-value">{user.teachers.join(', ')}</span>
            </Attribute>
                )}
                {/* The number itself is the Phone row above — this is the
                    student's own opt-in, which only they can change. */}
                <Attribute label="WhatsApp reminders">
                  <span className="sui-attr-value">
                    {!user.phone
                      ? <em style={{ color: 'var(--text-tertiary)' }}>no number</em>
                      : user.whatsappReminders ? 'on ✓' : 'off'}
                  </span>
            </Attribute>
              </>
            )}
            <Attribute label="Auth Provider">
              <span className="sui-attr-value">{user.authProvider || 'email'}</span>
            </Attribute>
            <Attribute label="Ring">
              <span className="sui-attr-value">
                {isEditing ? (
                  <select className="ud-edit-select" value={editRing} onChange={e => setEditRing(e.target.value)} disabled={updateUserMutation.isPending}>
                    <option value="prod">prod</option>
                    <option value="beta">beta</option>
                    <option value="devtesting">devtesting</option>
                    <option value="guest">guest</option>
                  </select>
                ) : <span className={`ring-badge ring-${user.ring || 'prod'}`}>{user.ring || 'prod'}</span>}
              </span>
            </Attribute>
            <Attribute label="Pipeline Config">
              <span className="sui-attr-value" style={!isEditing && user.pipelineConfig ? { color: 'var(--color-error, #dc2626)', fontWeight: 600 } : undefined}>
                {isEditing ? (
                  <select className="ud-edit-select" value={editPipelineConfig} onChange={e => setEditPipelineConfig(e.target.value)} disabled={updateUserMutation.isPending}>
                    <option value="">(default)</option>
                    {pipelineConfigNames.filter(n => n !== 'default').map(name => <option key={name} value={name}>{name}</option>)}
                  </select>
                ) : (user.pipelineConfig || <em style={{ color: 'var(--text-tertiary)' }}>default</em>)}
              </span>
            </Attribute>
            <Attribute label="Notification Email">
              <span className="sui-attr-value ud-identity-value">
                {isEditing && canEditIdentity
                  ? <input type="email" className="ud-edit-input" value={editNotificationEmail} onChange={e => setEditNotificationEmail(e.target.value)} placeholder={user.email} disabled={updateUserMutation.isPending} />
                  : (user.notificationEmail || <em style={{ color: 'var(--text-tertiary)' }}>{user.email} (primary)</em>)}
                {isEditing && (
                  <span className="ud-field-info" title={IDENTITY_ONLY_SUPER_ADMIN} aria-label={IDENTITY_ONLY_SUPER_ADMIN} role="img">i</span>
                )}
              </span>
            </Attribute>
            <Attribute label="Roles">
              <span className="sui-attr-value" style={{ fontFamily: 'monospace', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                {canEditRoles && (
                  <button
                    className="ud-role-edit-btn"
                    onClick={() => setEditRolesOpen(true)}
                    title="Edit roles"
                    aria-label="Edit roles"
                  >
                    ✎
                  </button>
                )}
                {user.roles && user.roles.length > 0
                  ? user.roles.map(r => (
                      <span key={r} style={{ display: 'inline-block', padding: '2px 8px', marginRight: 4, background: 'var(--bg-tertiary)', borderRadius: 4 }}>{r}</span>
                    ))
                  : <em style={{ color: 'var(--text-tertiary)', fontFamily: 'inherit' }}>(none)</em>}
              </span>
            </Attribute>
            {!checkIsAdmin(user) && (
              <Attribute label={hasRole(user, 'teacher') ? 'Students' : 'Operations'}>
                <span className="sui-attr-value">
                  {hasRole(user, 'teacher')
                    ? (user.studentCount ?? '-')
                    /* The count is modelInputs — every model call, hints included
                       — so it linked to a Solution Analysis list that could be
                       empty while the number said 1. No link: there is no single
                       tab this number lands on, since one operation could be a
                       hint, an extraction or an analysis. */
                    : (user.operationCount || 0)}
                </span>
            </Attribute>
            )}
            <Attribute label="Last Active">
              <span className="sui-attr-value">{formatLastActive(user.lastActivityAt)}</span>
            </Attribute>
            {/* Per-platform last-open. Rendered only for platforms actually seen,
                so a web-only user doesn't show an "iOS app — Never" row implying
                the app exists for them. Absent entirely for users who predate
                the user-dyninfo container. */}
            {user.lastOpenAt?.ios && (
              <Attribute label="iOS app">
                <span className="sui-attr-value">
                  {formatLastActive(user.lastOpenAt.ios)}
                  {user.lastAppVersion?.ios && ` · v${user.lastAppVersion.ios}`}
                </span>
            </Attribute>
            )}
            {user.lastOpenAt?.android && (
              <Attribute label="Android app">
                <span className="sui-attr-value">
                  {formatLastActive(user.lastOpenAt.android)}
                  {user.lastAppVersion?.android && ` · v${user.lastAppVersion.android}`}
                </span>
            </Attribute>
            )}
            {user.lastOpenAt?.web && (
              <Attribute label="Web">
                <span className="sui-attr-value">{formatLastActive(user.lastOpenAt.web)}</span>
            </Attribute>
            )}
            <Attribute label="Joined">
              <span className="sui-attr-value">{format(new Date(user.createdAt), 'MMM d, yyyy')}</span>
            </Attribute>
            {/* Where this account came from — its first touch, written once at
                creation. Spans the row: an ad landing carries a path, a
                referrer, and tags. "not recorded" is an account from before
                capture, never a guess. */}
            <Attribute label="Came from" span>
              <span className="sui-attr-value" title={user.acquisition ? JSON.stringify(user.acquisition, null, 2) : undefined}>
                {user.acquisition
                  ? describeAcquisition(user.acquisition)
                  : <em style={{ color: 'var(--text-tertiary)' }}>not recorded</em>}
              </span>
            </Attribute>

            {/* Inside the grid, where a full-width row can actually span. It used
                to sit outside it carrying `gridColumn: 1 / -1`, which does nothing
                to a element that is not a grid item. */}
            {user.deletedAt && (
            <Attribute label="Deleted" span>
              {/* Was inline light-mode hexes, which rendered a near-white banner
                  whatever the theme. */}
              <span className="sui-attr-value ud-deleted-value">
                {formatDistanceToNow(new Date(user.deletedAt), { addSuffix: true })}
              </span>
            </Attribute>
            )}
          </AttributeGrid>

          {/* Per-user admin actions — Impersonate + Delete/Restore on one line.
           *  Roles are edited via the ✎ icon on the Roles row above; the inline
           *  field form is saved from the header. Visible only to tenant_admin /
           *  super-admin viewers, matching the backend's auth checks. */}
          {viewerCanAdmin && (
            <div className="user-info-card-actions">
              {canImpersonate && (
                <button
                  className="impersonate-btn"
                  onClick={handleImpersonate}
                  disabled={impersonateMutation.isPending}
                >
                  {impersonateMutation.isPending ? 'Opening...' : 'Impersonate'}
                </button>
              )}
              {impersonateMutation.isError && (
                <span className="status-error">
                  Failed: {(impersonateMutation.error as Error).message}
                </span>
              )}

              {!user.deletedAt && (
                <button
                  className="send-message-btn"
                  onClick={() => setSendMessageOpen(true)}
                >
                  Send message
                </button>
              )}

              {/* Super-admin only — the backend refuses anyone else. */}
              {me?.isSuperAdmin && !user.deletedAt && (
                <button
                  className="send-message-btn"
                  onClick={() => setSetPasswordOpen(true)}
                >
                  Set password
                </button>
              )}

              {user.deletedAt ? (
                <>
                  <button
                    className="restore-user-btn"
                    onClick={handleRestore}
                    disabled={restoreMutation.isPending}
                  >
                    {restoreMutation.isPending ? 'Restoring...' : 'Restore User'}
                  </button>
                  {restoreMutation.isError && (
                    <span className="status-error">
                      Failed: {(restoreMutation.error as Error).message}
                    </span>
                  )}
                </>
              ) : (
                <>
                  <button
                    className="delete-user-btn"
                    onClick={handleDelete}
                    disabled={softDeleteMutation.isPending || hardDeleteMutation.isPending}
                  >
                    {(softDeleteMutation.isPending || hardDeleteMutation.isPending) ? 'Deleting...' : (isTestUser || (isGuestUser && viewerCanAdmin)) ? 'Hard Delete' : 'Delete User'}
                  </button>
                  {(softDeleteMutation.isError || hardDeleteMutation.isError) && (
                    <span className="status-error">
                      Failed: {((softDeleteMutation.error || hardDeleteMutation.error) as Error).message}
                    </span>
                  )}
                </>
              )}
            </div>
          )}

          <EditRolesModal
            isOpen={editRolesOpen}
            userId={user.id}
            userName={user.name}
            currentRoles={user.roles ?? []}
            onClose={() => setEditRolesOpen(false)}
          />

          <SendMessageModal
            isOpen={sendMessageOpen}
            userId={user.id}
            userName={user.name}
            onClose={() => setSendMessageOpen(false)}
          />

          <SetPasswordModal
            isOpen={setPasswordOpen}
            userId={user.id}
            userName={user.name}
            onClose={() => setSetPasswordOpen(false)}
          />

        </div>
      ) : (
        <div className="user-info-card">
          <h1>User {userId.substring(0, 8)}...</h1>
        </div>
      )}

      {viewerIsPlatform && (
        <>
        {/* Tab Bar */}
        <div className="user-detail-tabs">
          <button className={`user-detail-tab ${activeTab === 'activity' ? 'active' : ''}`} onClick={() => setActiveTab('activity')}>Activity</button>
          <button className={`user-detail-tab ${activeTab === 'subscriptions' ? 'active' : ''}`} onClick={() => setActiveTab('subscriptions')}>Subscriptions</button>
          <button className={`user-detail-tab ${activeTab === 'eligibility' ? 'active' : ''}`} onClick={() => setActiveTab('eligibility')}>Eligibility</button>
          <button className={`user-detail-tab ${activeTab === 'feature-gates' ? 'active' : ''}`} onClick={() => setActiveTab('feature-gates')}>Feature Gates</button>
          <button className={`user-detail-tab ${activeTab === 'experiments' ? 'active' : ''}`} onClick={() => setActiveTab('experiments')}>Experiments</button>
          <button className={`user-detail-tab ${activeTab === 'rate-limits' ? 'active' : ''}`} onClick={() => setActiveTab('rate-limits')}>Rate Limits</button>
          <button className={`user-detail-tab ${activeTab === 'costs' ? 'active' : ''}`} onClick={() => setActiveTab('costs')}>Costs</button>
          <button className={`user-detail-tab ${activeTab === 'logging' ? 'active' : ''}`} onClick={() => setActiveTab('logging')}>Logging</button>
          <button className={`user-detail-tab ${activeTab === 'sessions' ? 'active' : ''}`} onClick={() => setActiveTab('sessions')}>Sessions</button>

          <button className={`user-detail-tab ${activeTab === 'patterns' ? 'active' : ''}`} onClick={() => setActiveTab('patterns')}>Patterns</button>
          {(!user || userTypeFromRoles(user) === 'student') && (
            <button className={`user-detail-tab ${activeTab === 'teacher-controls' ? 'active' : ''}`} onClick={() => setActiveTab('teacher-controls')}>Teacher Controls</button>
          )}
          {/* Students only: it is guidance for reading a student's own page. */}
          {(!user || userTypeFromRoles(user) === 'student') && (
            <button className={`user-detail-tab ${activeTab === 'handwriting' ? 'active' : ''}`} onClick={() => setActiveTab('handwriting')}>Handwriting</button>
          )}
          {user && hasRole(user, 'teacher') && (
            <button className={`user-detail-tab ${activeTab === 'teacher-profile' ? 'active' : ''}`} onClick={() => setActiveTab('teacher-profile')}>Profile</button>
          )}
          {user && hasRole(user, 'teacher') && (
            <button className={`user-detail-tab ${activeTab === 'students' ? 'active' : ''}`} onClick={() => setActiveTab('students')}>Students</button>
          )}
          {user && hasRole(user, 'teacher') && (
            <button className={`user-detail-tab ${activeTab === 'help-queries' ? 'active' : ''}`} onClick={() => setActiveTab('help-queries')}>Help Queries</button>
          )}
        </div>

        {activeTab === 'eligibility' && <EligibilityTab userId={userId} />}
        {activeTab === 'feature-gates' && <FeatureGatesTab userId={userId} />}
        {activeTab === 'experiments' && <ExperimentsTab userId={userId} />}
        {activeTab === 'activity' && <ActivityTab userId={userId} />}
        {activeTab === 'sessions' && <SessionsTab userId={userId} />}
        {activeTab === 'patterns' && <PatternsTab userId={userId} />}
        {activeTab === 'subscriptions' && <SubscriptionsTab userId={userId} user={user ? { name: user.name, bypassSubscription: user.bypassSubscription } : undefined} />}
        {activeTab === 'rate-limits' && <RateLimitsTab userId={userId} userType={rateLimitUserType(user)} />}
        {activeTab === 'costs' && <CostsTab userId={userId} />}
        {activeTab === 'logging' && <LoggingTab userId={userId} />}
        {activeTab === 'teacher-controls' && <TeacherControlsTab userId={userId} />}
        {activeTab === 'handwriting' && <HandwritingTab userId={userId} value={user?.handwritingNotes} />}
        {activeTab === 'teacher-profile' && <TeacherProfileTab userId={userId} />}
        {activeTab === 'students' && <TeacherStudentsTab teacherId={userId} />}
        {activeTab === 'help-queries' && <HelpQueriesTab userId={userId} />}
        </>
      )}
    </div>
  );
}

